import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ExternalLink,
  Loader2,
  Send,
  ShieldCheck,
} from "lucide-react";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { PageError, PageLoader } from "@/components/ui/PageStates";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CopyButton } from "@/components/ui/CopyButton";
import { useContent } from "@/lib/content-context";
import { api, errMessage } from "@/lib/api";

const STEP_TITLES = ["Broker checklist", "Your details", "Trading account"];

const EMPTY = {
  registered_via_link: false,
  broker_verified: false,
  name: "",
  email: "",
  telegram: "",
  platform: "MT5",
  account_id: "",
  account_type: "standard",
  notes: "",
};

export default function FreeAccessPage() {
  const { data, loading, error } = useContent();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    document.title = "Free access - Goldmap EA";
    window.scrollTo({ top: 0 });
  }, []);

  const set = (key) => (value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((x) => ({ ...x, [key]: undefined }));
  };
  const onInput = (key) => (e) => set(key)(e.target.value);

  const validateStep = (index) => {
    const next = {};
    if (index === 0) {
      if (!form.registered_via_link)
        next.registered_via_link = "Tick this only after you register using our link.";
      if (!form.broker_verified)
        next.broker_verified = "Verification must be fully done before we can issue a licence.";
    }
    if (index === 1) {
      if (form.name.trim().length < 2) next.name = "Name at least 2 letters ya.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
        next.email = "Put a valid email - this is where the licence goes.";
      if (form.telegram.trim().length < 2)
        next.telegram = "Telegram handle needed so we can follow up.";
    }
    if (index === 2) {
      if (form.account_id.trim().length < 3)
        next.account_id = "Enter your MT4 or MT5 account ID.";
      else if (!form.account_id.trim().replace(/-/g, "").match(/^[a-zA-Z0-9]+$/))
        next.account_id = "Account ID should be letters and numbers only.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const next = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(s + 1, STEP_TITLES.length - 1));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validateStep(2)) return;
    setBusy(true);
    try {
      const res = await api.createFreeAccess({
        name: form.name.trim(),
        email: form.email.trim(),
        telegram: form.telegram.trim(),
        platform: form.platform,
        account_id: form.account_id.trim(),
        account_type: form.account_type,
        broker_verified: form.broker_verified,
        registered_via_link: form.registered_via_link,
        notes: form.notes.trim() || null,
      });
      setDone(res);
      toast.success(`Application received. Ref ${res.ref_code}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const pct = useMemo(() => ((step + 1) / STEP_TITLES.length) * 100, [step]);

  if (loading) {
    return (
      <SiteLayout config={null}>
        <PageLoader />
      </SiteLayout>
    );
  }
  if (error || !data) {
    return (
      <SiteLayout config={null}>
        <PageError onRetry={() => window.location.reload()} />
      </SiteLayout>
    );
  }

  const config = data.config;

  return (
    <SiteLayout config={config} availability={data.availability}>
      <section className="gm-hero-bg relative pb-8 pt-12">
        <div className="gm-container">
          <Link
            to="/"
            data-testid="free-access-back-link"
            className="inline-flex items-center gap-2 text-xs font-semibold"
            style={{ color: "var(--gm-muted)" }}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to home
          </Link>
          <h1 className="mt-5 text-3xl font-extrabold text-white sm:text-4xl">
            Apply for free access
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed" style={{ color: "var(--gm-muted)" }}>
            Free licence is funded by our broker partnership. Fill in the details below
            and we verify manually - usually within one working day.
          </p>
        </div>
      </section>

      <section className="gm-section pt-8">
        <div className="gm-container grid gap-6 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-7">
            {done ? (
              <div className="gm-card" data-testid="free-access-success">
                <span
                  className="inline-flex h-12 w-12 items-center justify-center rounded-full"
                  style={{
                    background: "rgba(43,213,118,0.12)",
                    border: "1px solid rgba(43,213,118,0.4)",
                  }}
                >
                  <Check className="h-6 w-6" style={{ color: "var(--gm-success)" }} />
                </span>
                <h2 className="mt-4 text-xl font-bold text-white">Application received</h2>
                <p className="mt-2 text-sm" style={{ color: "var(--gm-muted)" }}>
                  {done.message}
                </p>

                <div
                  className="mt-5 rounded-xl p-4"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.12)",
                  }}
                >
                  <p className="text-xs" style={{ color: "var(--gm-muted-2)" }}>
                    Your reference code
                  </p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-3">
                    <p
                      className="font-mono-gm text-2xl font-semibold"
                      data-testid="free-access-success-code"
                      style={{ color: "var(--gm-success)" }}
                    >
                      {done.ref_code}
                    </p>
                    <CopyButton
                      value={done.ref_code}
                      label="Copy code"
                      testId="free-access-copy-code-button"
                      toastMessage="Reference code copied."
                    />
                  </div>
                </div>

                <h3 className="mt-6 text-sm font-bold text-white">What happens next</h3>
                <ol className="mt-3 space-y-2.5">
                  {[
                    "We check your broker registration against our partner report.",
                    "Once confirmed, your licence is bound to that one account ID.",
                    "We send the EA files and licence key to your email and Telegram.",
                  ].map((t, i) => (
                    <li key={t} className="flex gap-3 text-sm" style={{ color: "var(--gm-muted)" }}>
                      <span className="gm-ring-num h-7 w-7 text-[11px]">{i + 1}</span>
                      <span className="pt-1">{t}</span>
                    </li>
                  ))}
                </ol>

                <div className="mt-6 flex flex-col gap-2 sm:flex-row">
                  <a
                    href={config?.telegram_owner || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="free-access-success-telegram"
                    className="gm-btn-red flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl text-sm font-bold"
                  >
                    <Send className="h-4 w-4" />
                    Message us on Telegram
                  </a>
                  <Link
                    to="/"
                    data-testid="free-access-success-home"
                    className="gm-btn-ghost flex min-h-[48px] items-center justify-center rounded-xl px-5 text-sm font-semibold"
                  >
                    Back to home
                  </Link>
                </div>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="gm-card"
                data-testid="free-access-form"
                noValidate
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="gm-eyebrow">
                    Step {step + 1} of {STEP_TITLES.length}
                  </p>
                  <p className="text-xs font-semibold text-white">{STEP_TITLES[step]}</p>
                </div>
                <Progress value={pct} className="mt-3 h-1.5" data-testid="free-access-progress" />

                {step === 0 ? (
                  <div className="mt-6 space-y-4">
                    <a
                      href={config?.broker_signup_url || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="free-access-broker-link"
                      className="gm-btn-ghost flex min-h-[48px] items-center justify-center gap-2 rounded-xl text-sm font-semibold"
                    >
                      Open the broker registration link
                      <ExternalLink className="h-4 w-4" />
                    </a>

                    {[
                      {
                        key: "registered_via_link",
                        label: "I registered a new broker account using the link above",
                        help: "Existing accounts cannot be attached to the referral.",
                      },
                      {
                        key: "broker_verified",
                        label: "My broker account is fully verified (KYC done)",
                        help: "Half-verified accounts will delay your licence.",
                      },
                    ].map((c) => (
                      <div
                        key={c.key}
                        className="rounded-xl p-4"
                        style={{
                          background: "rgba(255,255,255,0.03)",
                          border: `1px solid ${
                            errors[c.key] ? "rgba(255,90,122,0.5)" : "rgba(255,255,255,0.1)"
                          }`,
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id={c.key}
                            checked={form[c.key]}
                            onCheckedChange={(v) => set(c.key)(Boolean(v))}
                            data-testid={`free-access-${c.key}-checkbox`}
                            className="mt-0.5"
                          />
                          <div>
                            <Label htmlFor={c.key} className="text-sm text-white">
                              {c.label}
                            </Label>
                            <p className="mt-1 text-xs" style={{ color: "var(--gm-muted-2)" }}>
                              {c.help}
                            </p>
                            {errors[c.key] ? (
                              <p className="mt-1.5 text-xs" style={{ color: "var(--gm-danger)" }}>
                                {errors[c.key]}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {step === 1 ? (
                  <div className="mt-6 space-y-4">
                    <div>
                      <Label htmlFor="fa-name" className="text-white">
                        Full name
                      </Label>
                      <Input
                        id="fa-name"
                        value={form.name}
                        onChange={onInput("name")}
                        placeholder="As per your broker account"
                        data-testid="free-access-name-input"
                        className="mt-1.5"
                      />
                      {errors.name ? (
                        <p className="mt-1 text-xs" style={{ color: "var(--gm-danger)" }}>
                          {errors.name}
                        </p>
                      ) : null}
                    </div>
                    <div>
                      <Label htmlFor="fa-email" className="text-white">
                        Email
                      </Label>
                      <Input
                        id="fa-email"
                        type="email"
                        value={form.email}
                        onChange={onInput("email")}
                        placeholder="you@email.com"
                        data-testid="free-access-email-input"
                        className="mt-1.5"
                      />
                      {errors.email ? (
                        <p className="mt-1 text-xs" style={{ color: "var(--gm-danger)" }}>
                          {errors.email}
                        </p>
                      ) : null}
                    </div>
                    <div>
                      <Label htmlFor="fa-tg" className="text-white">
                        Telegram handle
                      </Label>
                      <Input
                        id="fa-tg"
                        value={form.telegram}
                        onChange={onInput("telegram")}
                        placeholder="@yourhandle"
                        data-testid="free-access-telegram-input"
                        className="mt-1.5"
                      />
                      {errors.telegram ? (
                        <p className="mt-1 text-xs" style={{ color: "var(--gm-danger)" }}>
                          {errors.telegram}
                        </p>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {step === 2 ? (
                  <div className="mt-6 space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <Label className="text-white">Platform</Label>
                        <Select value={form.platform} onValueChange={set("platform")}>
                          <SelectTrigger
                            className="mt-1.5"
                            data-testid="free-access-platform-select"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="MT5">MetaTrader 5</SelectItem>
                            <SelectItem value="MT4">MetaTrader 4</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-white">Account type</Label>
                        <Select value={form.account_type} onValueChange={set("account_type")}>
                          <SelectTrigger
                            className="mt-1.5"
                            data-testid="free-access-account-type-select"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="standard">Standard</SelectItem>
                            <SelectItem value="cent">Cent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="fa-acc" className="text-white">
                        Trading account ID
                      </Label>
                      <Input
                        id="fa-acc"
                        value={form.account_id}
                        onChange={onInput("account_id")}
                        placeholder="e.g. 5210934"
                        data-testid="free-access-account-input"
                        className="mt-1.5"
                      />
                      <p className="mt-1.5 text-xs" style={{ color: "var(--gm-muted-2)" }}>
                        The free licence locks to this one account ID, so double check
                        before submit.
                      </p>
                      {errors.account_id ? (
                        <p className="mt-1 text-xs" style={{ color: "var(--gm-danger)" }}>
                          {errors.account_id}
                        </p>
                      ) : null}
                    </div>
                    <div>
                      <Label htmlFor="fa-notes" className="text-white">
                        Notes <span style={{ color: "var(--gm-muted-2)" }}>(optional)</span>
                      </Label>
                      <Textarea
                        id="fa-notes"
                        rows={3}
                        value={form.notes}
                        onChange={onInput("notes")}
                        placeholder="Anything we should know"
                        data-testid="free-access-notes-input"
                        className="mt-1.5"
                      />
                    </div>
                  </div>
                ) : null}

                <div className="mt-7 flex items-center gap-3">
                  {step > 0 ? (
                    <button
                      type="button"
                      onClick={() => setStep((s) => s - 1)}
                      data-testid="free-access-back-button"
                      className="gm-btn-ghost inline-flex min-h-[48px] items-center gap-2 rounded-xl px-4 text-sm font-semibold"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Back
                    </button>
                  ) : null}

                  {step < STEP_TITLES.length - 1 ? (
                    <button
                      type="button"
                      onClick={next}
                      data-testid="free-access-next-button"
                      className="gm-btn-red inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl text-sm font-bold"
                    >
                      Continue
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="free-access-submit-button"
                      className="gm-btn-red inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl text-sm font-bold disabled:opacity-60"
                    >
                      {busy ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                      {busy ? "Submitting..." : "Submit application"}
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>

          <aside className="lg:col-span-5">
            <div className="gm-card">
              <h2 className="flex items-center gap-2 text-sm font-bold text-white">
                <ShieldCheck className="h-4 w-4" style={{ color: "var(--gm-success)" }} />
                Before you apply
              </h2>
              <ol className="mt-4 space-y-4">
                {(data.free_access_steps || []).map((s) => (
                  <li key={s.slug || s.no} className="flex gap-3">
                    <span className="gm-ring-num h-8 w-8 text-[11px]">{s.no}</span>
                    <div>
                      <p className="text-sm font-semibold text-white">{s.title}</p>
                      <p className="mt-1 text-xs leading-relaxed" style={{ color: "var(--gm-muted)" }}>
                        {s.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
              <p
                className="mt-5 rounded-xl p-3 text-xs leading-relaxed"
                style={{
                  background: "rgba(246,196,83,0.08)",
                  border: "1px solid rgba(246,196,83,0.28)",
                  color: "var(--gm-muted)",
                }}
              >
                One free licence per person, locked to one account ID. Kalau you need more
                accounts or a different broker, take a look at the paid options.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}
